import { Head } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import {
  FileText,
  FileSpreadsheet,
  FileType,
  File,
  FileArchive,
  FileAudio,
  FileVideo,
  Download,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventFile {
  id: number
  fileName: string
  url: string
  downloadUrl: string
  contentType: string
}

interface EventInfo {
  id: number
  name: string
  primaryColor: string | null
  font: string | null
  bannerUrl: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventInfo
  documents: EventFile[]
  images: EventFile[]
  urls: AppShellProps['urls']
  translations: AppShellProps['translations'] & {
    gallery: string
    noFiles: string
  }
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function fileIcon(ct: string) {
  if (/pdf/i.test(ct)) return <FileText className="h-5 w-5 text-red-500" />
  if (/word|doc/i.test(ct)) return <FileType className="h-5 w-5 text-blue-600" />
  if (/excel|sheet|csv/i.test(ct)) return <FileSpreadsheet className="h-5 w-5 text-green-600" />
  if (/zip|rar|7z/i.test(ct)) return <FileArchive className="h-5 w-5 text-yellow-600" />
  if (/audio|mp3|wav/i.test(ct)) return <FileAudio className="h-5 w-5 text-purple-500" />
  if (/video|mp4|mov/i.test(ct)) return <FileVideo className="h-5 w-5 text-pink-500" />
  return <File className="h-5 w-5 text-[#91918E]" />
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Links(props: Props) {
  const { event, documents, images, urls, translations: t, ...shell } = props

  const primary = event.primaryColor || '#37352F'
  const fontFamily = event.font || "'Poppins', sans-serif"
  const hasContent = documents.length > 0 || images.length > 0

  return (
    <AppShell urls={urls} translations={t} {...shell}>
      <Head title={event.name} />

      <div className="mx-auto max-w-3xl px-4 py-8" style={{ fontFamily }}>
        <div className="overflow-hidden rounded-2xl bg-white shadow-lg">
          {/* Banner */}
          {event.bannerUrl && (
            <div className="w-full">
              <img src={event.bannerUrl} alt={event.name} className="w-full object-cover" />
            </div>
          )}

          <div className="p-5 sm:p-8">
            {/* Title */}
            <h1 className="mb-8 text-2xl font-bold" style={{ color: primary }}>
              {event.name}
            </h1>

            {!hasContent && (
              <div className="rounded-lg bg-blue-50 p-4 text-center text-sm text-blue-700">
                {t.noFiles}
              </div>
            )}

            {/* Documents */}
            {documents.length > 0 && (
              <div className="space-y-3">
                {documents.map(doc => (
                  <a
                    key={doc.id}
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{ border: '1px solid #E8E8E5' }}
                  >
                    {fileIcon(doc.contentType)}
                    <span className="flex-1 text-sm font-medium" style={{ color: primary }}>
                      {doc.fileName}
                    </span>
                    <Download className="h-4 w-4 text-[#91918E]" />
                  </a>
                ))}
              </div>
            )}

            {/* Image gallery */}
            {images.length > 0 && (
              <>
                <h3 className="mb-5 mt-8 text-lg font-semibold" style={{ color: primary }}>
                  {t.gallery}
                </h3>
                <div className="grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-5">
                  {images.map(img => (
                    <a
                      key={img.id}
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group overflow-hidden rounded-xl border border-[#E8E8E5] bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <img
                        src={img.url}
                        alt={img.fileName}
                        className="h-44 w-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="p-3">
                        <p className="truncate text-sm font-medium text-[#555]">{img.fileName}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Footer */}
            <footer className="mt-10 border-t border-[#E8E8E5] pt-4 text-center">
              <small className="text-[#91918E]">
                &copy; {new Date().getFullYear()} {event.name}
              </small>
            </footer>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
